import { BookingFlowEmbeddedShell } from "components/booking-flow";
import BookingFlowConfirmationBody, {
    BookingFlowConfirmationCheckListItem,
} from "../booking-flow-confirmation-body";
import useAuthUser from "hooks/auth-user";
import useBookingFlowRedesign from "hooks/useBookingFlowRedesign";
import useLanguage from "hooks/language";
import { kebabCase } from "lodash";
import { DateTime } from "luxon";
import { useEffect } from "react";
import { useAsync } from "react-async-hook";
import { NotificationManager } from "react-notifications";
import { useHistory, useParams } from "react-router-dom";
import { DEFAULT_TIMEZONE } from "utils/constants";
import { createIcsFile } from "utils/generateIcsFile";
import { getLocalizedAppointmentDateWithTime } from "utils/private-service/date";
import { SCREENING_FLOW } from "models/screening-flow";
import routes from "utils/routes";
import { setScreeningFlow } from "utils/session-storage";
import { ReactComponent as Checkmark } from "../../../../assets/images/check-green.svg";
import { ReactComponent as Clock } from "../../../../assets/images/clock-icon.svg";
import { ReactComponent as Pin } from "../../../../assets/images/pin-icon.svg";
import IconInfoBlock from "../../../../components/icon-info-block";
import "./screening-appointment-confirmation.css";

export default function HpvAppointmentConfirmation(): JSX.Element | null {
    const { language, translations } = useLanguage();
    const [authUser] = useAuthUser();
    const isBookingFlowRedesign = useBookingFlowRedesign();

    const t = translations.hpvConfirmation;

    const { id } = useParams<{ id: string }>();

    const { replace } = useHistory();
    const appointment = authUser?.appointments.find(
        (appointment) => String(appointment.id) === id
    );

    useEffect(() => {
        setScreeningFlow(SCREENING_FLOW.HPV);
    }, []);

    useEffect(() => {
        if (!authUser) return;

        if (!appointment) {
            NotificationManager.error(
                translations.notification.unexpectedError
            );
            replace(routes.APPOINTMENTS);
        }
    }, [
        authUser,
        appointment,
        replace,
        translations.notification.unexpectedError,
    ]);

    const icsFileName = `${kebabCase(t.icsFileTitle)}.ics`;
    const { result: iCalendar } = useAsync(async () => {
        if (!appointment) {
            return undefined;
        }

        return await createIcsFile({
            eventLocation: appointment.clinic.name,
            eventDateTime: DateTime.fromISO(appointment.datetime, {
                zone: DEFAULT_TIMEZONE,
            }),
            fileName: icsFileName,
            title: t.title,
        });
    }, [appointment, icsFileName, t.title]);

    if (!authUser || !appointment) {
        return null;
    }

    const formattedAppointmentDate = getLocalizedAppointmentDateWithTime(
        appointment,
        language
    );
    const confirmationContent = (
        <div
            className={`container hpv-appointment-confirmation ${
                isBookingFlowRedesign
                    ? "hpv-appointment-confirmation--bookingFlow"
                    : ""
            }`}
        >
            {isBookingFlowRedesign ? (
                <BookingFlowConfirmationBody
                    appointment={appointment}
                    subtitle={t.subtitle}
                    remindersTitle={t.reminder.title}
                    reminderItems={
                        <>
                            <BookingFlowConfirmationCheckListItem>
                                <strong>
                                    {t.reminder.intravaginalProducts.title}
                                </strong>{" "}
                                {t.reminder.intravaginalProducts.content}
                            </BookingFlowConfirmationCheckListItem>
                            <BookingFlowConfirmationCheckListItem>
                                <strong>{t.reminder.heavyPeriod.title}</strong>{" "}
                                {t.reminder.heavyPeriod.content}
                            </BookingFlowConfirmationCheckListItem>
                            <BookingFlowConfirmationCheckListItem>
                                <strong>{t.reminder.cancellation.title}</strong>{" "}
                                {t.reminder.cancellation.content}
                            </BookingFlowConfirmationCheckListItem>
                        </>
                    }
                    googleCalendarLink={appointment.addToGoogleCalendarLink}
                    iCalendarUrl={iCalendar?.url}
                    icsFileName={icsFileName}
                />
            ) : (
                <>
                    <div>
                        <Checkmark className="hpv-appointment-confirmation__icon" />
                        <h2 className="hpv-appointment-confirmation__title">
                            {t.title}
                        </h2>
                        <div className="hpv-appointment-confirmation__subtitle">
                            {t.subtitle}
                        </div>
                    </div>
                    <IconInfoBlock Icon={Pin} value={appointment.clinic.name} />
                    <IconInfoBlock
                        Icon={Clock}
                        label={t.appointment}
                        value={formattedAppointmentDate}
                    />
                    <div className="hpv-appointment-confirmation__actions">
                        <a
                            className="hpv-appointment-confirmation__action-link"
                            target="_blank"
                            rel="noreferrer"
                            href={appointment.addToGoogleCalendarLink}
                        >
                            {t.googleCalendar}
                        </a>
                        {iCalendar && (
                            <a
                                className="hpv-appointment-confirmation__action-link"
                                href={iCalendar.url}
                                download={icsFileName}
                                aria-label={`Download calendar file: ${icsFileName}`}
                            >
                                {t.appleCalendar}
                            </a>
                        )}
                    </div>
                    <div className="hpv-appointment-confirmation__note">
                        <div className="hpv-appointment-confirmation__note-title">
                            {t.reminder.title}
                        </div>
                        <ul className="hpv-appointment-confirmation__note-list">
                            <li className="hpv-appointment-confirmation__note-item">
                                <strong>
                                    {t.reminder.intravaginalProducts.title}
                                </strong>{" "}
                                {t.reminder.intravaginalProducts.content}
                            </li>
                            <li className="hpv-appointment-confirmation__note-item">
                                <strong>{t.reminder.heavyPeriod.title}</strong>{" "}
                                {t.reminder.heavyPeriod.content}
                            </li>
                            <li className="hpv-appointment-confirmation__note-item">
                                <strong>{t.reminder.cancellation.title}</strong>{" "}
                                {t.reminder.cancellation.content}
                            </li>
                        </ul>
                    </div>
                </>
            )}
        </div>
    );

    if (isBookingFlowRedesign) {
        return (
            <BookingFlowEmbeddedShell
                currentStep="confirmation"
                skipConsultationStep
                compactTopPadding
            >
                {confirmationContent}
            </BookingFlowEmbeddedShell>
        );
    }

    return confirmationContent;
}
